// Social Links Management Tab
// Date: January 27, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, SocialLink } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

export const SocialLinksTab: React.FC = () => {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    platform_name: '',
    url: '',
    icon_name: '',
    display_order: 0,
  });

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getAllSocialLinks();
      setLinks(data.sort((a, b) => a.display_order - b.display_order));
    } catch (error: any) {
      console.error('Error loading social links:', error);
      toast.error(error.response?.data?.message || 'Failed to load social links');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.platform_name.trim() || !formData.url.trim()) {
      toast.error('Platform name and URL are required');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await contentManagementService.updateSocialLink(editingId, formData);
        toast.success('Social link updated successfully');
      } else {
        await contentManagementService.createSocialLink(formData);
        toast.success('Social link created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ platform_name: '', url: '', icon_name: '', display_order: 0 });
      await loadLinks();
    } catch (error: any) {
      console.error('Error saving social link:', error);
      toast.error(error.response?.data?.message || 'Failed to save social link');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (link: SocialLink) => {
    setEditingId(link.id);
    setFormData({
      platform_name: link.platform_name,
      url: link.url,
      icon_name: link.icon_name || '',
      display_order: link.display_order,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this social link?')) {
      return;
    }

    try {
      await contentManagementService.deleteSocialLink(id);
      toast.success('Social link deleted successfully');
      await loadLinks();
    } catch (error: any) {
      console.error('Error deleting social link:', error);
      toast.error(error.response?.data?.message || 'Failed to delete social link');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">Social Media Links</h3>
        <Button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setFormData({ platform_name: '', url: '', icon_name: '', display_order: links.length });
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Social Link
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit' : 'Add'} Social Link</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Platform Name *
                </label>
                <input
                  type="text"
                  value={formData.platform_name}
                  onChange={(e) => setFormData({ ...formData, platform_name: e.target.value })}
                  placeholder="e.g., LinkedIn, Twitter, Facebook"
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Icon Name (optional)
                </label>
                <input
                  type="text"
                  value={formData.icon_name}
                  onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                  placeholder="e.g., linkedin, twitter"
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setFormData({ platform_name: '', url: '', icon_name: '', display_order: 0 });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {links.length === 0 ? (
            <p className="text-sm text-[var(--color-secondary-text)] text-center py-4">
              No social links found. Click &quot;Add Social Link&quot; to create one.
            </p>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="flex items-center justify-between p-4 border border-[var(--color-border)] rounded-lg"
                >
                  <div>
                    <p className="font-medium text-[var(--color-surface-text)]">{link.platform_name}</p>
                    <p className="text-sm text-[var(--color-secondary-text)]">{link.url}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(link)}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(link.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

